import { useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import Chip from "@mui/material/Chip";
import { Link, useSearchParams } from "react-router-dom";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import GameIcon from "components/admin/GameIcon";
import TemplatePicker from "components/admin/TemplatePicker";
import SelectField from "components/admin/SelectField";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import useConfirmChanges from "components/admin/confirmChanges";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatNumber } from "utils/format";
import PetCoinShopTab from "layouts/shop/PetCoinShop";
import VipTab from "layouts/shop/Vip";

const priceText = (prices) =>
  prices.map((p) => `${formatNumber(p.value)} ${p.money}`).join(" / ") || "-";

function ShopItemDialog({ item, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [prices, setPrices] = useState(item.prices.map((p) => p.value));
  const [count, setCount] = useState(item.count);
  const [dailyLimit, setDailyLimit] = useState(item.dailyLimit);
  const submit = async () => {
    const priceObj = (values) =>
      Object.fromEntries(item.prices.map((p, i) => [`p${i}`, values[i]]));
    const yes = await ask({
      title: `Lưu ${item.name}`,
      before: {
        ...priceObj(item.prices.map((p) => p.value)),
        count: item.count,
        dailyLimit: item.dailyLimit,
      },
      after: { ...priceObj(prices), count, dailyLimit },
      fields: [
        ...item.prices.map((p, i) => [`p${i}`, `Giá (${p.money})`]),
        ["count", "Số lượng mỗi lần mua"],
        ["dailyLimit", "Lượt mua mỗi ngày (0 = không giới hạn)"],
      ],
      notes: ["Người chơi thấy giá mới ở lần mở cửa hàng tiếp theo."],
    });
    if (!yes) return;
    const { ok, result } = await run(
      () =>
        api.put(`/api/admin/shop/npc/${item.id}`, {
          price: prices.map(Number),
          count: Number(count),
          dailyLimit: Number(dailyLimit),
        }),
      "Đã lưu dòng hàng"
    );
    if (ok) {
      onClose();
      onDone(result);
    }
  };
  return (
    <ActionDialog
      open
      title={`Sửa ${item.name}`}
      description="Đổi giá bán, số lượng nhận mỗi lần mua và số lượt mua mỗi ngày của món này trong cửa hàng NPC."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        {item.prices.map((p, i) => (
          <Grid item xs={6} key={p.type}>
            <MDInput
              fullWidth
              type="number"
              label={`Giá (${p.money})`}
              value={prices[i]}
              inputProps={{ min: 1 }}
              onChange={(e) => setPrices(prices.map((v, j) => (j === i ? e.target.value : v)))}
            />
          </Grid>
        ))}
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Số lượng mỗi lần mua"
            value={count}
            inputProps={{ min: 1 }}
            onChange={(e) => setCount(e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Lượt mua mỗi ngày (0 = không giới hạn)"
            value={dailyLimit}
            inputProps={{ min: 0 }}
            onChange={(e) => setDailyLimit(e.target.value)}
          />
        </Grid>
      </Grid>
      <Alert severity="info">Áp dụng ngay cho lần mở cửa hàng tiếp theo, lưu luôn vào DB.</Alert>
    </ActionDialog>
  );
}

ShopItemDialog.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    count: PropTypes.number,
    dailyLimit: PropTypes.number,
    prices: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function NpcShopTab() {
  const canEdit = useCan(PERMISSIONS.SHOP_EDIT);
  const shops = useFetch(() => api.get("/api/admin/shop/npc"), []);
  const [shopId, setShopId] = useState(null);
  const [editing, setEditing] = useState(null);
  const list = shops.data || [];
  const currentId = shopId ?? list[0]?.shopId ?? "";
  const current = list.find((s) => s.shopId === currentId);

  const replaceItem = (updated) =>
    shops.setData(
      list.map((s) => ({
        ...s,
        items: s.items.map((i) => (i.id === updated.id ? updated : i)),
      }))
    );

  return (
    <PageCard
      title="Cửa hàng NPC"
      subtitle="Món không có nút Sửa là hàng cố định trong code game, chỉ xem được"
      noPadding
      actions={
        <MDInput
          select
          size="small"
          value={currentId}
          onChange={(e) => setShopId(e.target.value)}
          sx={{ minWidth: 220, background: "white", borderRadius: 1 }}
          SelectProps={{ sx: { height: 36 } }}
        >
          {list.map((s) => (
            <MenuItem key={s.shopId} value={s.shopId}>
              {s.name} ({s.items.length})
            </MenuItem>
          ))}
        </MDInput>
      }
    >
      {shops.error && <Alert severity="error">{shops.error}</Alert>}
      <AdminTable
        loading={shops.loading}
        rows={current?.items || []}
        rowKey={(r, i) => `${r.id}-${i}`}
        emptyText="Cửa hàng trống"
        columns={[
          {
            key: "name",
            label: "Hàng",
            render: (r) => (
              <MDBox display="flex" gap={1} alignItems="center">
                <GameIcon path={r.icon} size={28} fallback={r.isPet ? "pets" : "inventory_2"} />
                {r.name}
                <MDTypography variant="caption" color="text">
                  #{r.templateId}
                </MDTypography>
              </MDBox>
            ),
          },
          { key: "count", label: "Số lượng", align: "right" },
          { key: "price", label: "Giá", render: (r) => priceText(r.prices) },
          {
            key: "dailyLimit",
            label: "Lượt mỗi ngày",
            align: "right",
            render: (r) => (r.dailyLimit > 0 ? r.dailyLimit : "-"),
          },
          {
            key: "clanLvl",
            label: "Cần cấp bang",
            align: "right",
            render: (r) => (r.clanLvl > 0 ? r.clanLvl : "-"),
          },
          canEdit && {
            key: "actions",
            label: "",
            align: "right",
            render: (r) =>
              r.editable && (
                <MDButton size="small" variant="text" color="warning" onClick={() => setEditing(r)}>
                  <Icon>edit</Icon>&nbsp;Sửa
                </MDButton>
              ),
          },
        ].filter(Boolean)}
      />
      {editing && (
        <ShopItemDialog item={editing} onClose={() => setEditing(null)} onDone={replaceItem} />
      )}
    </PageCard>
  );
}

const ARENA_LIMITS = [
  { value: 0, label: "Không giới hạn" },
  { value: 1, label: "Mỗi ngày" },
  { value: 2, label: "Mỗi tuần" },
  { value: 3, label: "Mỗi mùa đấu trường" },
];

const EMPTY_ARENA_ITEM = {
  itemId: null,
  count: 1,
  price: 100,
  limitType: 1,
  limitCount: 5,
  sort: 0,
  enabled: true,
  isLock: true,
};

/** Bảng `shoparena` chứa hai shop (cột shop): 0 shop đấu trường, 1 shop đấu trường sinh tử */
const ARENA_SHOPS = {
  0: {
    name: "shop đấu trường",
    money: "điểm đấu trường",
    season: "mùa Đấu trường thần thú",
    slots: "các ô mảnh pet",
  },
  1: {
    name: "shop đấu trường sinh tử",
    money: "điểm lôi đài",
    season: "mùa lôi đài",
    slots: "các ô mảnh trang bị",
  },
};

function ArenaShopDialog({ shop, item, nextSort, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const info = ARENA_SHOPS[shop];
  const [form, setForm] = useState(() =>
    item ? { ...item } : { ...EMPTY_ARENA_ITEM, sort: nextSort }
  );
  const set = (patch) => setForm({ ...form, ...patch });
  const submit = async () => {
    const yes = await ask({
      title: item ? `Lưu ${item.name}` : `Thêm hàng vào ${info.name}`,
      isNew: !item,
      before: item,
      after: form,
      fields: [
        ["itemId", "Vật phẩm (mã)"],
        ["price", `Giá (${info.money})`],
        ["count", "Số lượng mỗi lượt mua"],
        [
          "limitType",
          "Kiểu giới hạn",
          { text: (v) => ARENA_LIMITS.find((l) => l.value === Number(v))?.label || v },
        ],
        ["limitCount", "Số lượt mỗi kỳ"],
        ["sort", "Thứ tự"],
        ["enabled", "Đang bán"],
        ["isLock", "Khoá giao dịch"],
      ],
      notes: [
        !item &&
          `Bán ${form.count} món giá ${formatNumber(Number(form.price) || 0)} ${
            info.money
          }, ${ARENA_LIMITS.find((l) => l.value === Number(form.limitType))?.label.toLowerCase()}${
            Number(form.limitType) > 0 ? ` ${form.limitCount} lượt` : ""
          }.`,
        "Áp dụng ngay cho mọi người chơi.",
      ],
    });
    if (!yes) return;
    const body = {
      itemId: Number(form.itemId) || 0,
      count: Number(form.count),
      price: Number(form.price),
      limitType: Number(form.limitType),
      limitCount: Number(form.limitCount),
      sort: Number(form.sort),
      enabled: Boolean(form.enabled),
      isLock: Boolean(form.isLock),
      shop,
    };
    const { ok } = await run(
      () =>
        item
          ? api.put(`/api/admin/shop/arena/${item.id}`, body)
          : api.post("/api/admin/shop/arena", body),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  return (
    <ActionDialog
      open
      title={item ? `Sửa ${item.name}` : `Thêm hàng ${info.name}`}
      description={`Người chơi trả bằng ${info.money}. Giới hạn lượt tính riêng cho từng người trong mỗi kỳ (ngày tính từ 0h, tuần từ thứ Hai, mùa theo ${info.season}).`}
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TemplatePicker
            kind="item"
            label="Vật phẩm"
            value={form.itemId}
            hideAdminItems
            onChange={(itemId) => set({ itemId })}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label={`Giá (${info.money})`}
            value={form.price}
            inputProps={{ min: 1 }}
            onChange={(e) => set({ price: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Số lượng mỗi lượt mua"
            value={form.count}
            inputProps={{ min: 1 }}
            onChange={(e) => set({ count: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <SelectField
            fullWidth
            label="Giới hạn lượt mua"
            value={form.limitType}
            onChange={(e) => set({ limitType: Number(e.target.value) })}
          >
            {ARENA_LIMITS.map((l) => (
              <MenuItem key={l.value} value={l.value}>
                {l.label}
              </MenuItem>
            ))}
          </SelectField>
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Số lượt mỗi kỳ"
            value={form.limitCount}
            disabled={Number(form.limitType) === 0}
            inputProps={{ min: 1 }}
            onChange={(e) => set({ limitCount: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Thứ tự (nhỏ đứng trước)"
            value={form.sort}
            onChange={(e) => set({ sort: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <MDBox display="flex" flexDirection="column">
            <MDBox display="flex" alignItems="center">
              <Switch
                checked={Boolean(form.enabled)}
                onChange={(e) => set({ enabled: e.target.checked })}
              />
              <MDTypography variant="caption">Đang bán</MDTypography>
            </MDBox>
            <MDBox display="flex" alignItems="center">
              <Switch
                checked={Boolean(form.isLock)}
                onChange={(e) => set({ isLock: e.target.checked })}
              />
              <MDTypography variant="caption">Khoá giao dịch đồ mua ra</MDTypography>
            </MDBox>
          </MDBox>
        </Grid>
      </Grid>
    </ActionDialog>
  );
}

ArenaShopDialog.defaultProps = { item: null, nextSort: 0 };

ArenaShopDialog.propTypes = {
  shop: PropTypes.number.isRequired,
  item: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  nextSort: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function ArenaPetConfigDialog({ config, maxSlots, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState(() => ({ ...config }));
  const set = (patch) => setForm({ ...form, ...patch });
  const submit = async () => {
    const yes = await ask({
      title: "Lưu cấu hình mảnh pet",
      before: config,
      after: form,
      fields: [
        ["slots", "Số ô mảnh pet"],
        ["limit", "Số mảnh mỗi lần bốc"],
        ["priceLow", "Giá mảnh hạ giới"],
        ["priceSky", "Giá mảnh thiên đình"],
        ["refreshPrice", "Giá làm mới"],
        ["exclude", "Danh sách pet không bán"],
      ],
      notes: ["Áp dụng ngay cho mọi người chơi."],
    });
    if (!yes) return;
    const body = {
      slots: Number(form.slots),
      limit: Number(form.limit),
      priceLow: Number(form.priceLow),
      priceSky: Number(form.priceSky),
      refreshPrice: Number(form.refreshPrice),
      exclude: form.exclude || "",
    };
    const { ok } = await run(
      () => api.put("/api/admin/shop/arena/pet-config", body),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  const field = (key, label, min) => (
    <MDInput
      fullWidth
      type="number"
      label={label}
      value={form[key]}
      inputProps={{ min }}
      onChange={(e) => set({ [key]: e.target.value })}
    />
  );
  return (
    <ActionDialog
      open
      title="Mảnh pet ngẫu nhiên"
      description="Mỗi người có các ô mảnh pet bốc riêng: chưa trùng sinh chỉ bốc pet hạ giới, đã trùng sinh chỉ bốc pet thiên đình. Tự bốc lại miễn phí lúc 0h, người chơi trả điểm để làm mới sớm."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          {field("slots", `Số ô mảnh pet (0 - ${maxSlots}, 0 = tắt)`, 0)}
        </Grid>
        <Grid item xs={6}>
          {field("limit", "Số mảnh mua được mỗi lần bốc", 1)}
        </Grid>
        <Grid item xs={6}>
          {field("priceLow", "Giá 1 mảnh pet hạ giới (điểm)", 1)}
        </Grid>
        <Grid item xs={6}>
          {field("priceSky", "Giá 1 mảnh pet thiên đình (điểm)", 1)}
        </Grid>
        <Grid item xs={6}>
          {field("refreshPrice", "Giá làm mới (điểm)", 0)}
        </Grid>
        <Grid item xs={12}>
          <MDInput
            fullWidth
            multiline
            label="Id pet không bán mảnh (cách nhau dấu phẩy)"
            helperText="Pet tiến hoá và pet nhận từ trùng sinh thú cưng đã tự bị loại"
            value={form.exclude}
            onChange={(e) => set({ exclude: e.target.value })}
          />
        </Grid>
      </Grid>
    </ActionDialog>
  );
}

ArenaPetConfigDialog.propTypes = {
  config: PropTypes.shape({
    slots: PropTypes.number,
    limit: PropTypes.number,
    priceLow: PropTypes.number,
    priceSky: PropTypes.number,
    refreshPrice: PropTypes.number,
    exclude: PropTypes.string,
  }).isRequired,
  maxSlots: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function PetPoolGroup({ title, pets, color, showReason }) {
  return (
    <MDBox mb={1.5}>
      <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
        {title} ({pets.length})
      </MDTypography>
      <MDBox display="flex" gap={0.5} flexWrap="wrap">
        {pets.map((p) => (
          <Chip
            key={p.itemId}
            size="small"
            color={color}
            variant="outlined"
            icon={<GameIcon path={p.icon} size={18} fallback="pets" />}
            label={`${p.petName} #${p.petId}${showReason && p.reason ? ` - ${p.reason}` : ""}`}
          />
        ))}
      </MDBox>
    </MDBox>
  );
}

PetPoolGroup.defaultProps = { color: "default", showReason: false };

PetPoolGroup.propTypes = {
  title: PropTypes.string.isRequired,
  pets: PropTypes.arrayOf(PropTypes.object).isRequired,
  color: PropTypes.string,
  showReason: PropTypes.bool,
};

function ArenaPetCard() {
  const canEdit = useCan(PERMISSIONS.SHOP_EDIT);
  const data = useFetch(() => api.get("/api/admin/shop/arena/pet-config"), []);
  const [editing, setEditing] = useState(false);
  const config = data.data?.config;
  const pool = data.data?.pool || [];
  const low = pool.filter((p) => !p.reason && !p.sky);
  const sky = pool.filter((p) => !p.reason && p.sky);
  const excluded = pool.filter((p) => p.reason);
  return (
    <PageCard
      title="Mảnh pet ngẫu nhiên"
      subtitle={
        config
          ? `${config.slots} ô, mỗi ô ${config.limit} mảnh, hạ giới ${formatNumber(
              config.priceLow
            )} / thiên đình ${formatNumber(config.priceSky)} điểm một mảnh, làm mới ${formatNumber(
              config.refreshPrice
            )} điểm`
          : "Đang tải..."
      }
      color="info"
      actions={
        canEdit &&
        config && (
          <MDButton size="small" color="white" onClick={() => setEditing(true)}>
            <Icon>tune</Icon>&nbsp;Cấu hình
          </MDButton>
        )
      }
    >
      {data.error && <Alert severity="error">{data.error}</Alert>}
      <PetPoolGroup title="Bể hạ giới (người chưa trùng sinh)" pets={low} color="success" />
      <PetPoolGroup title="Bể thiên đình (người đã trùng sinh)" pets={sky} color="info" />
      <PetPoolGroup title="Không bốc" pets={excluded} showReason />
      {editing && config && (
        <ArenaPetConfigDialog
          config={config}
          maxSlots={data.data.maxSlots}
          onClose={() => setEditing(false)}
          onDone={() => data.reload(true)}
        />
      )}
    </PageCard>
  );
}

function ArenaFixedShop({ shop }) {
  const canEdit = useCan(PERMISSIONS.SHOP_EDIT);
  const info = ARENA_SHOPS[shop];
  const data = useFetch(() => api.get(`/api/admin/shop/arena?shop=${shop}`), [shop]);
  const [editing, setEditing] = useState(null);
  const [run] = useRunner();
  const confirm = useConfirm();
  const rows = data.data || [];
  const nextSort = rows.reduce((max, r) => Math.max(max, r.sort), 0) + 10;

  const remove = async (r) => {
    const yes = await confirm({
      title: "Xoá hàng",
      message: `Xoá ${r.name} khỏi ${info.name}? Muốn ngừng bán tạm thời thì tắt "Đang bán" thay vì xoá.`,
      color: "error",
      confirmText: "Xoá",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.del(`/api/admin/shop/arena/${r.id}`),
      (text) => text
    );
    if (ok) data.reload(true);
  };

  return (
    <PageCard
      title="Hàng cố định"
      subtitle={`Bày sau ${info.slots}, trả bằng ${info.money}, sửa xong áp dụng ngay${
        shop === 0 ? ". Làm mới cửa hàng không đặt lại lượt của các món này" : ""
      }`}
      color="success"
      noPadding
      actions={
        canEdit && (
          <MDButton size="small" color="white" onClick={() => setEditing("new")}>
            <Icon>add</Icon>&nbsp;Thêm hàng
          </MDButton>
        )
      }
    >
      {data.error && <Alert severity="error">{data.error}</Alert>}
      <AdminTable
        loading={data.loading}
        rows={rows}
        rowKey="id"
        emptyText={`${info.name} trống`}
        columns={[
          { key: "sort", label: "Thứ tự", width: 70, align: "right" },
          {
            key: "name",
            label: "Hàng",
            render: (r) => (
              <MDBox display="flex" gap={1} alignItems="center">
                <GameIcon path={r.icon} size={28} fallback="inventory_2" />
                <MDBox lineHeight={1.2}>
                  <MDTypography variant="button" display="block">
                    {r.name}
                    {r.count > 1 ? ` x${r.count}` : ""}
                  </MDTypography>
                  <MDTypography variant="caption" color={r.problem ? "error" : "text"}>
                    #{r.itemId}
                    {r.problem ? ` - ${r.problem}` : ""}
                  </MDTypography>
                </MDBox>
              </MDBox>
            ),
          },
          {
            key: "price",
            label: "Giá (điểm)",
            align: "right",
            render: (r) => formatNumber(r.price),
          },
          {
            key: "limit",
            label: "Giới hạn",
            render: (r) => (r.limitType > 0 ? `${r.limitName}: ${r.limitCount} lượt` : r.limitName),
          },
          {
            key: "state",
            label: "Trạng thái",
            render: (r) => (
              <MDBox display="flex" gap={0.5} flexWrap="wrap">
                <Chip
                  size="small"
                  color={r.enabled ? "success" : "default"}
                  label={r.enabled ? "Đang bán" : "Tắt"}
                />
                {r.isLock && <Chip size="small" variant="outlined" label="Khoá GD" />}
              </MDBox>
            ),
          },
          canEdit && {
            key: "actions",
            label: "",
            align: "right",
            render: (r) => (
              <MDBox display="flex" justifyContent="flex-end">
                <MDButton size="small" variant="text" color="warning" onClick={() => setEditing(r)}>
                  <Icon>edit</Icon>&nbsp;Sửa
                </MDButton>
                <MDButton size="small" variant="text" color="error" onClick={() => remove(r)}>
                  <Icon>delete</Icon>
                </MDButton>
              </MDBox>
            ),
          },
        ].filter(Boolean)}
      />
      {editing && (
        <ArenaShopDialog
          shop={shop}
          item={editing === "new" ? null : editing}
          nextSort={nextSort}
          onClose={() => setEditing(null)}
          onDone={() => data.reload(true)}
        />
      )}
    </PageCard>
  );
}

ArenaFixedShop.propTypes = {
  shop: PropTypes.number.isRequired,
};

function ArenaShopTab() {
  return (
    <>
      <MDBox mb={3}>
        <ArenaPetCard />
      </MDBox>
      <ArenaFixedShop shop={0} />
    </>
  );
}

function ArenaEquipConfigDialog({ config, maxSlots, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState(() => ({ ...config }));
  const set = (patch) => setForm({ ...form, ...patch });
  const submit = async () => {
    const yes = await ask({
      title: "Lưu cấu hình mảnh trang bị",
      before: config,
      after: form,
      fields: [
        ["slots", "Số ô"],
        ["limit", "Số mảnh mỗi ô mỗi ngày"],
        ["priceLow", "Giá mảnh hạ giới"],
        ["priceSky", "Giá mảnh thượng giới"],
        ["poolLow", "Bể hạ giới"],
        ["poolSky", "Bể thượng giới"],
      ],
      notes: ["Áp dụng ngay cho mọi người chơi."],
    });
    if (!yes) return;
    const body = {
      slots: Number(form.slots),
      limit: Number(form.limit),
      priceLow: Number(form.priceLow),
      priceSky: Number(form.priceSky),
      poolLow: form.poolLow || "",
      poolSky: form.poolSky || "",
    };
    const { ok } = await run(
      () => api.put("/api/admin/shop/arena/equip-config", body),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  const field = (key, label, min) => (
    <MDInput
      fullWidth
      type="number"
      label={label}
      value={form[key]}
      inputProps={{ min }}
      onChange={(e) => set({ [key]: e.target.value })}
    />
  );
  return (
    <ActionDialog
      open
      title="Mảnh trang bị ngẫu nhiên"
      description="Mỗi người có các ô mảnh trang bị bốc riêng: chưa mở thượng giới chỉ bốc bể hạ giới, đã mở chỉ bốc bể thượng giới. Tự bốc lại lúc 0h, không có làm mới bằng điểm."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          {field("slots", `Số ô (0 - ${maxSlots}, 0 = tắt)`, 0)}
        </Grid>
        <Grid item xs={6}>
          {field("limit", "Số mảnh mua được mỗi ô mỗi ngày", 1)}
        </Grid>
        <Grid item xs={6}>
          {field("priceLow", "Giá 1 mảnh hạ giới (điểm lôi đài)", 1)}
        </Grid>
        <Grid item xs={6}>
          {field("priceSky", "Giá 1 mảnh thượng giới (điểm lôi đài)", 1)}
        </Grid>
        <Grid item xs={12}>
          <MDInput
            fullWidth
            multiline
            label="Bể hạ giới: id mảnh, cách nhau dấu phẩy"
            value={form.poolLow}
            onChange={(e) => set({ poolLow: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            fullWidth
            multiline
            label="Bể thượng giới: id mảnh, cách nhau dấu phẩy"
            value={form.poolSky}
            onChange={(e) => set({ poolSky: e.target.value })}
          />
        </Grid>
      </Grid>
    </ActionDialog>
  );
}

ArenaEquipConfigDialog.propTypes = {
  config: PropTypes.shape({
    slots: PropTypes.number,
    limit: PropTypes.number,
    priceLow: PropTypes.number,
    priceSky: PropTypes.number,
    poolLow: PropTypes.string,
    poolSky: PropTypes.string,
  }).isRequired,
  maxSlots: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function EquipPoolGroup({ title, items, color }) {
  return (
    <MDBox mb={1.5}>
      <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
        {title} ({items.length})
      </MDTypography>
      <MDBox display="flex" gap={0.5} flexWrap="wrap">
        {items.map((p) => (
          <Chip
            key={p.itemId}
            size="small"
            color={p.problem ? "error" : color}
            variant="outlined"
            icon={<GameIcon path={p.icon} size={18} fallback="inventory_2" />}
            label={`${p.name}${p.problem ? ` - ${p.problem}` : ""}`}
          />
        ))}
      </MDBox>
    </MDBox>
  );
}

EquipPoolGroup.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  color: PropTypes.string.isRequired,
};

function ArenaEquipCard() {
  const canEdit = useCan(PERMISSIONS.SHOP_EDIT);
  const data = useFetch(() => api.get("/api/admin/shop/arena/equip-config"), []);
  const [editing, setEditing] = useState(false);
  const config = data.data?.config;
  return (
    <PageCard
      title="Mảnh trang bị ngẫu nhiên"
      subtitle={
        config
          ? `${config.slots} ô, mỗi ô ${config.limit} mảnh mỗi ngày, hạ giới ${formatNumber(
              config.priceLow
            )} / thượng giới ${formatNumber(
              config.priceSky
            )} điểm lôi đài một mảnh, không có làm mới`
          : "Đang tải..."
      }
      color="info"
      actions={
        canEdit &&
        config && (
          <MDButton size="small" color="white" onClick={() => setEditing(true)}>
            <Icon>tune</Icon>&nbsp;Cấu hình
          </MDButton>
        )
      }
    >
      {data.error && <Alert severity="error">{data.error}</Alert>}
      <EquipPoolGroup
        title="Bể hạ giới (chưa mở thượng giới)"
        items={data.data?.low || []}
        color="success"
      />
      <EquipPoolGroup
        title="Bể thượng giới (đã mở thượng giới)"
        items={data.data?.sky || []}
        color="info"
      />
      {editing && config && (
        <ArenaEquipConfigDialog
          config={config}
          maxSlots={data.data.maxSlots}
          onClose={() => setEditing(false)}
          onDone={() => data.reload(true)}
        />
      )}
    </PageCard>
  );
}

function ArenaEventShopTab() {
  return (
    <>
      <MDBox mb={3}>
        <ArenaEquipCard />
      </MDBox>
      <ArenaFixedShop shop={1} />
    </>
  );
}

function ExchangeDialog({ pack, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [amount, setAmount] = useState(pack.amount);
  const [gold, setGold] = useState(pack.gold);
  const submit = async () => {
    const rate = (a, g) =>
      Number(a) > 0 ? formatNumber(Math.round((Number(g) * 10) / Number(a))) : "-";
    const yes = await ask({
      title: "Lưu gói đổi vàng",
      before: pack,
      after: { amount, gold },
      fields: [
        ["amount", "Pet Coin trả"],
        ["gold", "Vàng nhận"],
      ],
      notes: [
        `Tỉ giá: ${rate(pack.amount, pack.gold)} → ${rate(amount, gold)} vàng mỗi 10 Pet Coin.`,
        "Áp dụng ngay cho lần đổi tiếp theo.",
      ],
    });
    if (!yes) return;
    const { ok } = await run(
      () =>
        api.put(`/api/admin/shop/exchange/${pack.id}`, {
          amount: Number(amount),
          gold: Number(gold),
        }),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  return (
    <ActionDialog
      open
      title={`Sửa gói đổi ${formatNumber(pack.amount)} Pet Coin`}
      description="Gói người chơi dùng Pet Coin (tiền nạp, 1 Pet Coin = 100đ) để đổi lấy vàng trong game. Sửa có hiệu lực ngay và lưu vào DB."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <MDInput
        type="number"
        label="Số Pet Coin trả"
        value={amount}
        inputProps={{ min: 1 }}
        onChange={(e) => setAmount(e.target.value)}
      />
      <MDInput
        type="number"
        label="Số vàng nhận"
        value={gold}
        inputProps={{ min: 1 }}
        onChange={(e) => setGold(e.target.value)}
      />
    </ActionDialog>
  );
}

ExchangeDialog.propTypes = {
  pack: PropTypes.shape({ id: PropTypes.number, amount: PropTypes.number, gold: PropTypes.number })
    .isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function ExchangeTab() {
  const canEdit = useCan(PERMISSIONS.SHOP_EDIT);
  const canPlayer = useCan(PERMISSIONS.PLAYER_VIEW);
  const data = useFetch(() => api.get("/api/admin/shop/exchange"), []);
  const [editing, setEditing] = useState(null);
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <PageCard
          title="Gói đổi Pet Coin ra vàng"
          subtitle="Menu đổi vàng trong game, sửa xong áp dụng ngay"
          color="success"
          noPadding
        >
          {data.error && <Alert severity="error">{data.error}</Alert>}
          <AdminTable
            loading={data.loading}
            rows={data.data?.packages || []}
            rowKey="id"
            emptyText="Chưa có gói"
            columns={[
              {
                key: "amount",
                label: "Pet Coin",
                align: "right",
                render: (p) => `${formatNumber(p.amount)} (≈ ${formatNumber(p.amount * 100)}đ)`,
              },
              { key: "gold", label: "Vàng", align: "right", render: (p) => formatNumber(p.gold) },
              {
                key: "rate",
                label: "Vàng / 10 Pet Coin",
                align: "right",
                render: (p) =>
                  p.amount > 0 ? formatNumber(Math.round((p.gold * 10) / p.amount)) : "-",
              },
              canEdit && {
                key: "actions",
                label: "",
                align: "right",
                render: (p) => (
                  <MDButton
                    size="small"
                    variant="text"
                    color="warning"
                    onClick={() => setEditing(p)}
                  >
                    <Icon>edit</Icon>&nbsp;Sửa
                  </MDButton>
                ),
              },
            ].filter(Boolean)}
          />
        </PageCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <PageCard
          title="Vàng nạp thẻ đang chờ cộng"
          subtitle="Tự cộng khi người chơi đăng nhập lần tới"
          color="warning"
          noPadding
        >
          <AdminTable
            loading={data.loading}
            rows={data.data?.pending || []}
            rowKey="id"
            emptyText="Không có"
            columns={[
              {
                key: "name",
                label: "Người chơi",
                render: (p) =>
                  canPlayer ? (
                    <MDTypography
                      component={Link}
                      to={`/players/${p.userId}`}
                      variant="button"
                      color="info"
                    >
                      {p.name || `#${p.userId}`}
                    </MDTypography>
                  ) : (
                    p.name || `#${p.userId}`
                  ),
              },
              { key: "gold", label: "Vàng", align: "right", render: (p) => formatNumber(p.gold) },
              {
                key: "id",
                label: "Mã giao dịch",
                render: (p) => (
                  <MDTypography variant="caption" color="text">
                    {p.id}
                  </MDTypography>
                ),
              },
            ]}
          />
        </PageCard>
      </Grid>
      {editing && (
        <ExchangeDialog
          pack={editing}
          onClose={() => setEditing(null)}
          onDone={() => data.reload(true)}
        />
      )}
    </Grid>
  );
}

const TAB_KEYS = ["npc", "arena", "arena-event", "petcoin", "exchange", "vip"];
const TAB_SX = { flex: { xs: "0 0 auto", xl: "1 1 auto" }, px: 1.5, whiteSpace: "nowrap" };

function Shop() {
  const canVip = useCan(PERMISSIONS.VIP_VIEW);
  const [params, setParams] = useSearchParams();
  // Tab ghi lên URL (?tab=) để gửi link thẳng tới một shop.
  const raw = params.get("tab");
  const tab = TAB_KEYS.includes(raw) && (raw !== "vip" || canVip) ? raw : "npc";
  const setTab = (v) => setParams(v === "npc" ? {} : { tab: v }, { replace: true });
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="shop" />
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(e, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="npc" label="Cửa hàng NPC" sx={TAB_SX} />
            <Tab value="arena" label="Shop đấu trường" sx={TAB_SX} />
            <Tab value="arena-event" label="Shop đấu trường sinh tử" sx={TAB_SX} />
            <Tab value="petcoin" label="Shop Pet Coin (tiền nạp)" sx={TAB_SX} />
            <Tab value="exchange" label="Đổi Pet Coin ra vàng" sx={TAB_SX} />
            {canVip && <Tab value="vip" label="VIP" sx={TAB_SX} />}
          </Tabs>
        </Card>
        {tab === "npc" && <NpcShopTab />}
        {tab === "arena" && <ArenaShopTab />}
        {tab === "arena-event" && <ArenaEventShopTab />}
        {tab === "petcoin" && <PetCoinShopTab />}
        {tab === "exchange" && <ExchangeTab />}
        {tab === "vip" && canVip && <VipTab />}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Shop;
